import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SmsProviderTestResult } from '@/api/notificationProviders/notificationProvidersApi.types';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phoneInput';
import { useSendSmsProviderTestMutation } from '@/hooks/useNotificationProviders';
import { cn } from '@/lib/utils';
import { ProviderField } from '../ProviderField';
import { presentSmsTestResult, type SmsTestResultTone } from './smsTestResultPresentation';

const TEST_RECIPIENT_INPUT_ID = 'sms-provider-test-recipient';

const RESULT_TONE_CLASSES: Record<SmsTestResultTone, string> = {
  success: 'bg-zoosuccessbg border-zoosuccessborder text-zoosuccesstext',
  warning: 'bg-zoowarningbg border-zooborder text-zoowarningtext',
  error: 'bg-zooerrorbg border-zooerrorborder text-zooerrortext',
};

interface SmsProviderTestPanelProps {
  isConfigured: boolean;
}

interface SmsTestResultNoticeProps {
  result: SmsProviderTestResult;
}

function SmsTestResultNotice({ result }: SmsTestResultNoticeProps) {
  const { t } = useTranslation('notifications');
  const presentation = presentSmsTestResult(result);
  return (
    <div
      role="status"
      className={cn(
        'mt-3 rounded-xl border px-3 py-2 text-sm',
        RESULT_TONE_CLASSES[presentation.tone]
      )}
    >
      <p>{t(presentation.messageKey)}</p>
      {presentation.providerErrorCode !== null ? (
        <p className="text-xs mt-1 opacity-80">
          {t('providers.sms.providerErrorCode', { code: presentation.providerErrorCode })}
        </p>
      ) : null}
    </div>
  );
}

export function SmsProviderTestPanel({ isConfigured }: SmsProviderTestPanelProps) {
  const { t } = useTranslation('notifications');
  const sendTest = useSendSmsProviderTestMutation();
  const [testRecipient, setTestRecipient] = useState('');
  const [latestResult, setLatestResult] = useState<SmsProviderTestResult | null>(null);
  const [hasRequestFailed, setHasRequestFailed] = useState(false);

  function test() {
    setLatestResult(null);
    setHasRequestFailed(false);
    sendTest.mutate(testRecipient, {
      onSuccess: setLatestResult,
      onError: function reportRequestFailure() {
        setHasRequestFailed(true);
      },
    });
  }

  return (
    <div className="mt-4 pt-4 border-t border-zooborder">
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="flex-1">
          <ProviderField label={t('providers.sms.testRecipient')} htmlFor={TEST_RECIPIENT_INPUT_ID}>
            <PhoneInput
              inputId={TEST_RECIPIENT_INPUT_ID}
              value={testRecipient}
              onChange={setTestRecipient}
              isDisabled={isConfigured === false}
            />
          </ProviderField>
        </div>
        <Button
          variant="outline"
          onClick={test}
          disabled={isConfigured === false || testRecipient.length === 0}
          isLoading={sendTest.isPending}
        >
          {t('providers.test')}
        </Button>
      </div>
      <p className="text-xs text-zoomuted mt-1.5">
        {isConfigured ? t('providers.sms.testHint') : t('providers.sms.testDisabledHint')}
      </p>
      {sendTest.isPending ? (
        <p className="text-xs text-zoomuted mt-2">{t('providers.sms.testInProgress')}</p>
      ) : null}
      {latestResult !== null ? <SmsTestResultNotice result={latestResult} /> : null}
      {hasRequestFailed ? (
        <p role="alert" className="mt-3 text-sm text-zooerrortext">
          {t('providers.testFailed')}
        </p>
      ) : null}
    </div>
  );
}
